import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  FormControlLabel,
  Checkbox,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  Slider,
} from '@material-ui/core';
import { databaseService } from '../../services/databaseService';
import {
  CreateDatabaseRequest,
  DatabaseEngine,
  StorageLocation,
  MonitoringEndpoint,
} from '../../types';

interface CreateDatabaseDialogProps {
  open: boolean;
  onClose: () => void;
  storageLocations: StorageLocation[];
  monitoringEndpoints: MonitoringEndpoint[];
}

const steps = ['Basic Settings', 'Resources', 'Advanced Options'];

export const CreateDatabaseDialog: React.FC<CreateDatabaseDialogProps> = ({
  open,
  onClose,
  storageLocations,
  monitoringEndpoints,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [availableVersions, setAvailableVersions] = useState<string[]>([]);

  const [formData, setFormData] = useState<CreateDatabaseRequest>({
    name: '',
    namespace: 'everest',
    engine: DatabaseEngine.POSTGRESQL,
    version: '',
    replicas: 3,
    cpu: 1,
    memory: 2,
    storageSize: 20,
    storageClass: 'standard',
    enableBackups: false,
    backupSchedule: '0 2 * * *',
    backupStorage: '',
    enableMonitoring: false,
    monitoringEndpoint: '',
    exposureType: 'internal',
  });

  useEffect(() => {
    let cancelled = false;

    const loadVersions = async (engine: DatabaseEngine) => {
      const versions = await databaseService.getAvailableVersions(engine);
      if (cancelled) return;
      setAvailableVersions(versions);
      if (versions.length > 0) {
        setFormData(prev => ({ ...prev, version: versions[0] }));
      }
    };

    if (formData.engine) {
      loadVersions(formData.engine);
    }

    return () => {
      cancelled = true;
    };
  }, [formData.engine]);

  const handleNext = () => {
    setActiveStep(prevStep => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };

  const handleChange = (field: keyof CreateDatabaseRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      await databaseService.createCluster(formData);
      onClose();
      setFormData({
        name: '',
        namespace: 'everest',
        engine: DatabaseEngine.POSTGRESQL,
        version: '',
        replicas: 3,
        cpu: 1,
        memory: 2,
        storageSize: 20,
        storageClass: 'standard',
        enableBackups: false,
        backupSchedule: '0 2 * * *',
        backupStorage: '',
        enableMonitoring: false,
        monitoringEndpoint: '',
        exposureType: 'internal',
      });
      setActiveStep(0);
    } catch (error) {
      console.error('Error creating database:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Database Name"
                value={formData.name}
                onChange={e => handleChange('name', e.target.value)}
                required
                helperText="Use lowercase letters, numbers, and hyphens"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Namespace"
                value={formData.namespace}
                onChange={e => handleChange('namespace', e.target.value)}
              >
                <MenuItem value="everest">everest</MenuItem>
                <MenuItem value="production">production</MenuItem>
                <MenuItem value="staging">staging</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Database Engine"
                value={formData.engine}
                onChange={e =>
                  handleChange('engine', e.target.value as DatabaseEngine)
                }
              >
                <MenuItem value={DatabaseEngine.POSTGRESQL}>
                  PostgreSQL
                </MenuItem>
                <MenuItem value={DatabaseEngine.MYSQL}>MySQL</MenuItem>
                <MenuItem value={DatabaseEngine.MONGODB}>MongoDB</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Version"
                value={formData.version}
                onChange={e => handleChange('version', e.target.value)}
              >
                {availableVersions.map(version => (
                  <MenuItem key={version} value={version}>
                    {version}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography gutterBottom>
                Number of Replicas: {formData.replicas}
              </Typography>
              <Slider
                value={formData.replicas}
                onChange={(_e, value) => handleChange('replicas', value)}
                min={1}
                max={7}
                step={2}
                marks
                valueLabelDisplay="auto"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography gutterBottom>CPU Cores: {formData.cpu}</Typography>
              <Slider
                value={formData.cpu}
                onChange={(_e, value) => handleChange('cpu', value)}
                min={1}
                max={16}
                marks
                valueLabelDisplay="auto"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography gutterBottom>
                Memory (GB): {formData.memory}
              </Typography>
              <Slider
                value={formData.memory}
                onChange={(_e, value) => handleChange('memory', value)}
                min={1}
                max={64}
                marks={[1, 2, 4, 8, 16, 32, 64].map(v => ({
                  value: v,
                  label: `${v}`,
                }))}
                valueLabelDisplay="auto"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography gutterBottom>
                Storage (GB): {formData.storageSize}
              </Typography>
              <Slider
                value={formData.storageSize}
                onChange={(_e, value) => handleChange('storageSize', value)}
                min={10}
                max={1000}
                step={10}
                valueLabelDisplay="auto"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Storage Class"
                value={formData.storageClass}
                onChange={e => handleChange('storageClass', e.target.value)}
              />
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.enableBackups}
                    onChange={e =>
                      handleChange('enableBackups', e.target.checked)
                    }
                  />
                }
                label="Enable Automated Backups"
              />
            </Grid>
            {formData.enableBackups && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    label="Backup Storage"
                    value={formData.backupStorage}
                    onChange={e =>
                      handleChange('backupStorage', e.target.value)
                    }
                  >
                    {storageLocations.map(storage => (
                      <MenuItem
                        key={storage.metadata.name}
                        value={storage.metadata.name}
                      >
                        {storage.metadata.name} ({storage.spec.type})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Backup Schedule (Cron)"
                    value={formData.backupSchedule}
                    onChange={e =>
                      handleChange('backupSchedule', e.target.value)
                    }
                    helperText="E.g., '0 2 * * *' for daily at 2 AM"
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.enableMonitoring}
                    onChange={e =>
                      handleChange('enableMonitoring', e.target.checked)
                    }
                  />
                }
                label="Enable Monitoring (PMM)"
              />
            </Grid>
            {formData.enableMonitoring && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  label="Monitoring Endpoint"
                  value={formData.monitoringEndpoint}
                  onChange={e =>
                    handleChange('monitoringEndpoint', e.target.value)
                  }
                >
                  {monitoringEndpoints.map(endpoint => (
                    <MenuItem
                      key={endpoint.metadata.name}
                      value={endpoint.metadata.name}
                    >
                      {endpoint.metadata.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Database</DialogTitle>
      <DialogContent>
        <Box mt={2}>
          <Stepper activeStep={activeStep}>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Box mt={4}>{renderStepContent(activeStep)}</Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button disabled={activeStep === 0} onClick={handleBack}>
          Back
        </Button>
        {activeStep === steps.length - 1 ? (
          <Button
            onClick={handleCreate}
            color="primary"
            variant="contained"
            disabled={loading || !formData.name || !formData.version}
          >
            {loading ? 'Creating...' : 'Create'}
          </Button>
        ) : (
          <Button onClick={handleNext} color="primary" variant="contained">
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
